import React from 'react';
import { Box, Text, HStack, VStack, Tooltip } from '@chakra-ui/react';
import { format } from 'date-fns';

export const CHART_STATUS = {
  queued: {
    label: 'Queued', bg: '#FEF9C3', color: '#854D0E', barColor: '#EAB308', dotColor: '#EAB308',
  },
  on_hold: {
    label: 'On Hold', bg: '#FFE4E6', color: '#9F1239', barColor: '#F43F5E', dotColor: '#F43F5E',
  },
  in_review: {
    label: 'Processing', bg: '#DBEAFE', color: '#1E40AF', barColor: '#3B82F6', dotColor: '#3B82F6',
  },
  rebuttal: {
    label: 'Rebuttal', bg: '#EDE9FE', color: '#5B21B6', barColor: '#7C3AED', dotColor: '#7C3AED',
  },
  completed: {
    label: 'Completed', bg: '#DCFCE7', color: '#166534', barColor: '#22C55E', dotColor: '#22C55E',
  },
};
export const STATUS_ORDER = ['queued', 'on_hold', 'in_review', 'rebuttal', 'completed'];

export const CHART_SUBHEADER = {
  bg:       '#EFF6FF', // blue-50
  color:    '#64748B', // blue-700
};

export function ChartStatusBadge({ status }) {
  const cfg = CHART_STATUS[status];
  if (!cfg) return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
  return (
    <Box display="flex" justifyContent="center" w="100%">
      <Box display="inline-flex" alignItems="center" px={3} py="4px" borderRadius="full"
        bg={cfg.bg} border="1px solid" borderColor={cfg.barColor} whiteSpace="nowrap">
        <Text fontSize="12px" fontWeight={500} color={cfg.color}>{cfg.label}</Text>
      </Box>
    </Box>
  );
}

export function BatchStatusIndicator({ subRows = [] }) {
  if (!subRows.length) return <Text color="gray.400" fontSize="13px">N/A</Text>;
  const counts = subRows.reduce((acc, c) => {
    const k = c.status ?? 'queued'; acc[k] = (acc[k] ?? 0) + 1; return acc; // status already mapped by backend
  }, {});
  const present = STATUS_ORDER.filter(s => counts[s] > 0);

  if (present.length === 1) {
    const cfg = CHART_STATUS[present[0]];
    return (
      <Box display="flex" justifyContent="center" w="100%">
        <Box display="inline-flex" alignItems="center" px={3} py="4px" borderRadius="full"
          bg={cfg.bg} border="1px solid" borderColor={cfg.barColor} whiteSpace="nowrap">
          <Text fontSize="12px" fontWeight={500} color={cfg.color}>{cfg.label}</Text>
        </Box>
      </Box>
    );
  }
  return (
    <HStack spacing="6px" align="center" justify="center" w="100%">
      {present.map(s => {
        const cfg = CHART_STATUS[s];
        return (
          <Tooltip key={s}
            label={`${cfg.label}: ${counts[s]} chart${counts[s] !== 1 ? 's' : ''}`}
            placement="top" hasArrow bg="gray.700" color="white" fontSize="12px" borderRadius="md">
            <HStack spacing="4px" align="center" cursor="default" px={2} py="3px"
              borderRadius="full" bg={cfg.bg} border="1px solid" borderColor={cfg.barColor}
              _hover={{ opacity: 0.85 }} transition="opacity 0.15s">
              <Text fontSize="11px" fontWeight={600} color={cfg.color}>{counts[s]}</Text>
            </HStack>
          </Tooltip>
        );
      })}
    </HStack>
  );
}

export function BatchStatusIndicatorV2({ countDictionary }) {  
  return (
    <HStack spacing="6px" align="center" justify="center" w="100%">
      {Object.entries(countDictionary).map(([key, val]) => {
        const cfg = CHART_STATUS[key];
        return (
          <Tooltip key={key}
            label={`${cfg.label}: ${val} chart${val !== 1 ? 's' : ''}`}
            placement="top" hasArrow bg="gray.700" color="white" fontSize="12px" borderRadius="md">
            <HStack spacing="4px" align="center" cursor="default" px={2} py="3px"
              borderRadius="full" bg={cfg.bg} border="1px solid" borderColor={cfg.barColor}
              _hover={{ opacity: 0.85 }} transition="opacity 0.15s">
              <Text fontSize="11px" fontWeight={600} color={cfg.color}>{val}</Text>
            </HStack>
          </Tooltip>
        );
      })}
    </HStack>
  );
}

export function AttachmentCell({ attachments = [] }) {
  if (!attachments.length) return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
  const [first, ...rest] = attachments;
  const allTooltip = (
    <VStack align="flex-start" spacing="4px" p={1}>
      {attachments.map((f, i) => <Text key={i} fontSize="12px" whiteSpace="nowrap">{f}</Text>)}
    </VStack>
  );
  const tp = { label: allTooltip, placement: 'bottom', hasArrow: true, bg: 'gray.800', color: 'white', borderRadius: 'md', p: 2, openDelay: 80 };
  return (
    <HStack spacing="4px" align="center" justify="center" w="100%">
      <Tooltip {...tp}>
        <Text fontSize="12px" color="gray.600" isTruncated maxW="130px" cursor="default">{first}</Text>
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip {...tp}>
          <Text fontSize="11px" fontWeight={600} color="blue.500" cursor="default" flexShrink={0}>+{rest.length}</Text>
        </Tooltip>
      )}
    </HStack>
  );
}

export function CompletedDateCell({ completedDate, status }) {
  if (status !== 'completed' || !completedDate)
    return <Text color="gray.400" fontSize="14px" textAlign="center">N/A</Text>;
  return <Text textAlign="center" fontSize="14px">{format(new Date(completedDate), 'MM/dd/yyyy')}</Text>;
}

export function getBatchStatus(subRows = []) {
  if (!subRows.length) return null;
  if (subRows.every(c => c.status === 'completed')) return 'completed';
  if (subRows.some(c => c.status === 'in_review'))  return 'in_review';
  if (subRows.some(c => c.status === 'rebuttal'))   return 'rebuttal';
  if (subRows.some(c => c.status === 'on_hold'))    return 'on_hold';
  return 'queued';
}



export function NotesCell({ notes }) {
  if (!notes)
    return (
      <Text color="gray.400" fontSize="13px" textAlign="center">
        N/A
      </Text>
    );

  const truncateWords = (text, wordLimit = 3) => {
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(" ") + "...";
  };

  const tooltipContent = (
    <Box maxW="300px" maxH="200px" overflowY="auto">
      <Text fontSize="12px" whiteSpace="normal">
        {notes}
      </Text>
    </Box>
  );

  return (
    <Tooltip
      label={tooltipContent}
      placement="top"
      hasArrow
      bg="gray.800"
      color="white"
      borderRadius="md"
      p={2}
      openDelay={100}
    >
      <Text
        fontSize="13px"
        color="gray.600"
        textAlign="center"
        cursor="help"
      >
        {truncateWords(notes)}
      </Text>
    </Tooltip>
  );
}